import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const formatApiHistoryLimit = (value) => {
  if (value === null || value === undefined) return 'Not set';
  if (value === 0) return 'Latest year only';
  if (value >= 999) return 'All history';
  return `Up to ${value} year${value === 1 ? '' : 's'} back`;
};

const formatApiSourceLimit = (value) => {
  if (value === null || value === undefined || value === '') return 'All data sources';
  return value;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return 'Not set';
  return Number(value).toLocaleString();
};

const formatApiLimit = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  return Number(value).toLocaleString();
};

const formatDateOnly = (value, fallbackLabel = 'Unknown') => {
  if (!value) return fallbackLabel;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallbackLabel;
  return date.toLocaleDateString('en-US');
};

const formatDateTime = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const ApiKeys = ({ canManageKeys, serviceApiUrl, docsUrl, apiPermissions }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [createStatus, setCreateStatus] = useState({ type: '', message: '' });
  const [listStatus, setListStatus] = useState({ type: '', message: '' });
  const [latestSecret, setLatestSecret] = useState(null);
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmToggleKey, setConfirmToggleKey] = useState(null);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [detailsKey, setDetailsKey] = useState(null);
  const [keyFilter, setKeyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [headerCopied, setHeaderCopied] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  const resolvedServiceApiUrl = serviceApiUrl || 'https://api.finbaseco.com';
  const resolvedDocsUrl = docsUrl || 'https://finbaseco.com/docs';
  const serviceApiBaseUrl = resolvedServiceApiUrl.replace(/\/+$/, '');
  const exampleServiceApiPath = '/api/service/v1/data/fixed/il/sets?limit=5';
  const exampleCurlCommand = latestSecret
    ? `curl -H "Authorization: Bearer ${latestSecret.key_id_public}:${latestSecret.secret_key}" ${serviceApiBaseUrl}${exampleServiceApiPath}`
    : '';

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      setListStatus({ type: 'danger', message: 'Failed to copy to clipboard.' });
    }
  };

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await api.get('/acct-mgmt/v1/account/api-keys');
      setApiKeys(response.data);
    } catch (err) {
      setListStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load API keys.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName) return;
    setShowCreateConfirm(true);
  };

  const confirmCreateKey = async () => {
    setCreateStatus({ type: '', message: '' });
    try {
      const response = await api.post('/acct-mgmt/v1/account/api-keys', { friendly_name: newKeyName });
      setApiKeys((prev) => [...prev, { ...response.data, friendly_name: newKeyName, active_flag: true }]);
      setLatestSecret({
        id: response.data.id,
        key_id_public: response.data.key_id_public,
        secret_key: response.data.secret_key,
      });
      setNewKeyName('');
      fetchApiKeys();
      setCreateStatus({ type: 'success', message: 'API key created.' });
    } catch (err) {
      setCreateStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to create API key.' });
    } finally {
      setShowCreateConfirm(false);
    }
  };

  const toggleKeyStatus = async (key) => {
    try {
      const nextActive = !Boolean(key.active_flag);
      await api.put(`/acct-mgmt/v1/account/api-keys/${key.key_id_public}`, { active_flag: nextActive });
      setApiKeys((prev) => prev.map((k) => (k.key_id_public === key.key_id_public ? { ...k, active_flag: nextActive } : k)));
      fetchApiKeys();
      setListStatus({ type: 'success', message: `API Key Updated: ${key.friendly_name || 'Unnamed key'}` });
    } catch (err) {
      setListStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update API key.' });
    }
  };

  const startEdit = (key) => {
    setEditingKeyId(key.key_id_public);
    setEditingName(key.friendly_name || '');
    setListStatus({ type: '', message: '' });
  };

  const cancelEdit = () => {
    setEditingKeyId(null);
    setEditingName('');
  };

  const saveFriendlyName = async (key) => {
    try {
      await api.put(`/acct-mgmt/v1/account/api-keys/${key.key_id_public}`, { friendly_name: editingName });
      setApiKeys((prev) => prev.map((k) => (k.key_id_public === key.key_id_public ? { ...k, friendly_name: editingName } : k)));
      fetchApiKeys();
      setEditingKeyId(null);
      setListStatus({ type: 'success', message: `API Key Updated: ${editingName || key.friendly_name || 'Unnamed key'}` });
    } catch (err) {
      setListStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update API key.' });
    }
  };

  const apiRequestsCurrent = apiPermissions?.api_requests_current ?? 0;
  const apiRateLimit = apiPermissions?.api_rate_limit ?? null;
  const apiRemaining = apiRateLimit !== null ? Math.max(apiRateLimit - apiRequestsCurrent, 0) : null;
  const keyOptions = Array.from(new Set(apiKeys.map((key) => key.friendly_name).filter(Boolean)));
  const filteredKeys = apiKeys.filter((key) => {
    const matchesName = keyFilter ? key.friendly_name === keyFilter : true;
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'active'
        ? Boolean(key.active_flag)
        : !Boolean(key.active_flag);
    return matchesName && matchesStatus;
  });

  return (
    <div>
      <div className="mb-3">
        <h2 className="h4 fw-bold mb-1">API Keys</h2>
        <p className="text-muted mb-0">Manage API keys and view usage.</p>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">API Permissions</h5>
          <div className="row g-2">
            <div className="col-md-6">
              <div className="text-muted small fw-semibold mb-2">API Quotas</div>
              <div><strong>Monthly limit:</strong> {formatApiLimit(apiPermissions?.api_rate_limit)}</div>
              <div><strong>Requests this month:</strong> {formatNumber(apiRequestsCurrent)}</div>
              <div><strong>Remaining:</strong> {apiRemaining === null ? 'Unlimited' : formatNumber(apiRemaining)}</div>
              <div className="text-muted small mt-2">
                Usage figures are near real-time and may be delayed by a few minutes.
              </div>
            </div>
            <div className="col-md-6">
              <div className="text-muted small fw-semibold mb-2">Data Accessibility</div>
              <div>
                <strong>Data sources:</strong> {formatApiSourceLimit(apiPermissions?.api_src_limit)}
                <div className="text-muted small">Datasets your org can query. This list can expand as access changes.</div>
              </div>
              <div>
                <strong>Derived data:</strong> {apiPermissions?.api_src_derived_flag ? 'Allowed' : 'Not allowed'}
                <div className="text-muted small">Computed datasets based on underlying sources (premium feature).</div>
              </div>
              <div>
                <strong>History access:</strong> {formatApiHistoryLimit(apiPermissions?.api_hist_limit)}
                <div className="text-muted small">How far back your org can query historical data.</div>
              </div>
              <div className="text-muted small mt-2">Permissions reflect your organization’s current access.</div>
            </div>
            <div className="col-12">
              <div className="mt-2 pt-3 border-top">
                <div>
                  <div className="fw-semibold mb-1">API Documentation</div>
                  <div className="text-muted small mb-2">
                    Need help getting started? Review the quick start guide and API documentation.
                  </div>
                </div>
                <a
                  href={resolvedDocsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Open API Docs <i className="fas fa-external-link-alt ms-1" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {canManageKeys && (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">Create New API Key</h5>
            {createStatus.message && (
              <div className={`alert alert-${createStatus.type}`} role="alert">{createStatus.message}</div>
            )}
            <form onSubmit={handleCreateKey} className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Friendly name"
                required
              />
              <button type="submit" className="btn btn-primary">Create</button>
            </form>
          {latestSecret && (
            <div className="alert alert-warning mt-3" role="alert">
              <div className="fw-semibold">Copy this secret key now — you won’t see it again.</div>
              <div className="mt-2"><strong>Public key:</strong> {latestSecret.key_id_public}</div>
              <div><strong>Secret key:</strong> {latestSecret.secret_key}</div>
              <div className="mt-3">
                <div className="fw-semibold mb-2">How to use your API key</div>
                <div className="small text-muted mb-2">Include the Authorization header on every request:</div>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <pre className="bg-light p-2 rounded border small mb-0 flex-grow-1">
{`Authorization: Bearer ${latestSecret.key_id_public}:${latestSecret.secret_key}`}
                  </pre>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={async () => {
                      await copyToClipboard(`Authorization: Bearer ${latestSecret.key_id_public}:${latestSecret.secret_key}`);
                      setHeaderCopied(true);
                      setTimeout(() => setHeaderCopied(false), 2000);
                    }}
                  >
                    Copy header
                  </button>
                  {headerCopied && <div className="text-success small">Copied</div>}
                </div>
                <div className="small text-muted">Example (curl):</div>
                <div className="d-flex flex-wrap gap-2">
                  <pre className="bg-light p-2 rounded border small mb-0 flex-grow-1">
{exampleCurlCommand}
                  </pre>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={async () => {
                      await copyToClipboard(exampleCurlCommand);
                      setCurlCopied(true);
                      setTimeout(() => setCurlCopied(false), 2000);
                    }}
                  >
                    Copy curl
                  </button>
                  {curlCopied && <div className="text-success small">Copied</div>}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="mb-2">
            <h5 className="card-title">Manage API Keys</h5>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <a className="btn btn-link p-0" href="/account/api-keys/usage">
              View Usage
            </a>
            {!canManageKeys && (
              <div className="text-muted small d-flex align-items-center gap-2">
                <i className="fas fa-info-circle"></i>
                You can view keys and usage but cannot create or edit keys.
              </div>
            )}
          </div>
          {listStatus.message && (
            <div className={`alert alert-${listStatus.type}`} role="alert">{listStatus.message}</div>
          )}
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <label className="form-label">Filter by Key Name</label>
              <select
                className="form-select"
                value={keyFilter}
                onChange={(e) => setKeyFilter(e.target.value)}
              >
                <option value="">All keys</option>
                {keyOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Filter by Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
          {loading ? (
            <div className="text-muted">Loading API keys...</div>
          ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Public key</th>
                  <th>Created By</th>
                  <th>Creation Date</th>
                  <th>Last Utilized</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredKeys.map((key) => (
                  <tr key={key.key_id_public}>
                    <td>
                      {editingKeyId === key.key_id_public ? (
                        <div className="d-flex align-items-center gap-2">
                          <input
                            className="form-control"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => saveFriendlyName(key)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="d-inline-flex align-items-center gap-2">
                          {key.friendly_name}
                          {canManageKeys && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-muted"
                              aria-label="Edit API key name"
                              onClick={() => startEdit(key)}
                            >
                              <i className="fas fa-pen"></i>
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setDetailsKey(key)}
                      >
                        <i className="fas fa-key me-2"></i>
                        View Details
                      </button>
                    </td>
                    <td>{key.created_by_name || 'Unknown'}</td>
                    <td>{formatDateOnly(key.created_at)}</td>
                    <td>{formatDateOnly(key.last_utilized, 'Never')}</td>
                    <td>
                      <span className={`badge ${key.active_flag ? 'bg-success' : 'bg-secondary'}`}>
                        {key.active_flag ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => canManageKeys && setConfirmToggleKey({ key, nextActive: !key.active_flag })}
                        disabled={!canManageKeys}
                        title={canManageKeys ? '' : 'Requires Key Management role'}
                      >
                        {key.active_flag ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredKeys.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-muted text-center">No API keys created yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>
      {confirmToggleKey && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {confirmToggleKey.nextActive ? 'Activate API key?' : 'Deactivate API key?'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setConfirmToggleKey(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    {confirmToggleKey.nextActive
                      ? 'Activating this key will allow requests using it.'
                      : 'Deactivating this key will stop all requests using it. You can reactivate it later.'}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setConfirmToggleKey(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn ${confirmToggleKey.nextActive ? 'btn-primary' : 'btn-danger'}`}
                    onClick={async () => {
                      const targetKey = confirmToggleKey.key;
                      setConfirmToggleKey(null);
                      await toggleKeyStatus(targetKey);
                    }}
                  >
                    {confirmToggleKey.nextActive ? 'Yes, Activate' : 'Yes, Deactivate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showCreateConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create API key?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowCreateConfirm(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Create a new API key named <strong>{newKeyName}</strong>?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCreateKey}
                  >
                    Yes, Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {detailsKey && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">API key details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setDetailsKey(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-2"><strong>Name:</strong> {detailsKey.friendly_name}</div>
                  <div className="mb-2"><strong>Public key:</strong> {detailsKey.key_id_public}</div>
                  <div className="mb-2"><strong>Status:</strong> {detailsKey.active_flag ? 'Active' : 'Inactive'}</div>
                  <div className="mb-2"><strong>Last utilized:</strong> {detailsKey.last_utilized ? formatDateTime(detailsKey.last_utilized) : 'Never'}</div>
                  <div className="mb-2"><strong>Created by:</strong> {detailsKey.created_by_name || 'Unknown'}</div>
                  <div className="mb-2"><strong>Created:</strong> {formatDateTime(detailsKey.created_at)}</div>
                  <div className="mb-2"><strong>Last edited by:</strong> {detailsKey.updated_by_name || 'Unknown'}</div>
                  <div className="mb-0"><strong>Last edit:</strong> {formatDateTime(detailsKey.updated_at)}</div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setDetailsKey(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApiKeys;
